import { useState } from "react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { FileText, Target, TrendingUp, Users, Zap, Award, Video } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import EnhancedRegistration from "../components/EnhancedRegistration";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LandingPage({ onLogin }) {
  const [showAuth, setShowAuth] = useState(false);
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: "",
    role: "athlete"
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = isLogin ? "/auth/login" : "/auth/register";
      const payload = isLogin 
        ? { email: formData.email, password: formData.password }
        : formData;

      const response = await axios.post(`${API}${endpoint}`, payload);
      
      onLogin(response.data.token, response.data.user);
      toast.success(isLogin ? "Welcome back!" : "Account created successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-teal-50 via-emerald-50 to-green-100 min-h-screen">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(45,212,191,0.1),transparent_50%),radial-gradient(circle_at_70%_60%,rgba(16,185,129,0.08),transparent_50%)]"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 sm:pt-20 pb-8 sm:pb-16">
          {/* Logo */}
          <div className="flex justify-center mb-6 sm:mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_mindcoach-2/artifacts/t75zv7cu_Grow%20Sport%20Psychology%20Logo.svg"
              alt="Grow Sport Psychology Logo"
              className="h-20 sm:h-32 w-auto object-contain"
              style={{ filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1))' }}
            />
          </div>
          
          <div className="text-center">
            <h1 className="text-4xl sm:text-5xl md:text-7xl font-bold text-gray-900 mb-4 sm:mb-6 leading-tight">
              Unlock Your
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-teal-600 to-emerald-600">
                Mental Edge
              </span>
            </h1>
            <p className="text-base sm:text-xl md:text-2xl text-gray-700 mb-6 sm:mb-8 max-w-3xl mx-auto leading-relaxed px-4">
              Transform your athletic performance with sport psychology. 
              Build mental resilience and the winning mindset.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center mb-10 sm:mb-16 px-4">
              <Button 
                size="lg" 
                className="bg-teal-600 hover:bg-teal-700 text-white px-6 sm:px-8 py-4 text-base sm:text-lg font-semibold rounded-lg shadow-lg active:scale-95 transition-all duration-200"
                onClick={() => setShowAuth(true)}
              >
                Start Your Journey
                <Zap className="ml-2 w-5 h-5" />
              </Button>
              <Button 
                variant="outline" 
                size="lg"
                className="border-2 border-teal-600 text-teal-600 hover:bg-teal-50 px-6 sm:px-8 py-4 text-base sm:text-lg font-semibold rounded-lg active:scale-95 transition-all duration-200"
                onClick={() => setShowAuth(true)}
              >
                Learn More
              </Button>
            </div>
          </div>

          {!showAuth ? (
            <>
            {/* Features Section */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-8 mt-10 sm:mt-20">
              <Card className="border-0 shadow-lg transition-all duration-300 bg-white/80 backdrop-blur-sm">
                <CardHeader className="p-3 sm:p-6">
                  <FileText className="w-8 h-8 sm:w-12 sm:h-12 text-teal-600 mb-2 sm:mb-4" />
                  <CardTitle className="text-sm sm:text-xl">Mental Training</CardTitle>
                  <CardDescription className="text-xs sm:text-sm">
                    Visualization, breathing, and focus techniques
                  </CardDescription>
                </CardHeader>
              </Card>

              <Card className="border-0 shadow-lg transition-all duration-300 bg-white/80 backdrop-blur-sm">
                <CardHeader className="p-3 sm:p-6">
                  <Video className="w-8 h-8 sm:w-12 sm:h-12 text-red-600 mb-2 sm:mb-4" />
                  <CardTitle className="text-sm sm:text-xl">Video Library</CardTitle>
                  <CardDescription className="text-xs sm:text-sm">
                    Guided meditation and mental performance videos
                  </CardDescription>
                </CardHeader>
              </Card>

              <Card className="border-0 shadow-lg transition-all duration-300 bg-white/80 backdrop-blur-sm">
                <CardHeader className="p-3 sm:p-6">
                  <TrendingUp className="w-8 h-8 sm:w-12 sm:h-12 text-emerald-600 mb-2 sm:mb-4" />
                  <CardTitle className="text-sm sm:text-xl">Analytics</CardTitle>
                  <CardDescription className="text-xs sm:text-sm">
                    Track metrics and visualize progress
                  </CardDescription>
                </CardHeader>
              </Card>

              <Card className="border-0 shadow-lg transition-all duration-300 bg-white/80 backdrop-blur-sm">
                <CardHeader className="p-3 sm:p-6">
                  <Target className="w-8 h-8 sm:w-12 sm:h-12 text-purple-600 mb-2 sm:mb-4" />
                  <CardTitle className="text-sm sm:text-xl">AI Coach</CardTitle>
                  <CardDescription className="text-xs sm:text-sm">
                    Personalized insights powered by AI
                  </CardDescription>
                </CardHeader>
              </Card>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-8 mt-6 sm:mt-8">
              <Card className="border-0 shadow-lg transition-all duration-300 bg-white/80 backdrop-blur-sm">
                <CardHeader className="p-3 sm:p-6">
                  <Users className="w-8 h-8 sm:w-12 sm:h-12 text-emerald-600 mb-2 sm:mb-4" />
                  <CardTitle className="text-sm sm:text-xl">Team Management</CardTitle>
                  <CardDescription className="text-xs sm:text-sm">
                    Collaborate with coaches and teammates
                  </CardDescription>
                </CardHeader>
              </Card>

              <Card className="border-0 shadow-lg transition-all duration-300 bg-white/80 backdrop-blur-sm">
                <CardHeader className="p-3 sm:p-6">
                  <Target className="w-8 h-8 sm:w-12 sm:h-12 text-orange-600 mb-2 sm:mb-4" />
                  <CardTitle className="text-sm sm:text-xl">Goal Setting</CardTitle>
                  <CardDescription className="text-xs sm:text-sm">
                    Set and track personal goals
                  </CardDescription>
                </CardHeader>
              </Card>

              <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 bg-white/80 backdrop-blur-sm">
                <CardHeader>
                  <Award className="w-12 h-12 text-green-600 mb-4" />
                  <CardTitle className="text-xl">Progress Tracking</CardTitle>
                  <CardDescription>
                    Monitor your mental performance journey and celebrate achievements
                  </CardDescription>
                </CardHeader>
              </Card>
            </div>
            </>
          ) : isLogin ? (
            // Login Form
            <div className="max-w-md mx-auto mt-16">
              <Card className="border-0 shadow-2xl bg-white/90 backdrop-blur-md">
                <CardHeader className="space-y-1">
                  <CardTitle className="text-2xl text-center">Welcome Back</CardTitle>
                  <CardDescription className="text-center">
                    Sign in to continue your journey
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="email">Email</Label>
                      <Input
                        data-testid="login-email-input"
                        id="email"
                        type="email"
                        placeholder="you@example.com"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="password">Password</Label>
                      <Input
                        data-testid="login-password-input"
                        id="password"
                        type="password"
                        value={formData.password}
                        onChange={(e) => setFormData({...formData, password: e.target.value})}
                        required
                      />
                    </div>
                    <Button 
                      data-testid="login-submit-btn"
                      type="submit" 
                      className="w-full bg-teal-600 hover:bg-teal-700"
                      disabled={loading}
                    >
                      {loading ? "Signing in..." : "Sign In"}
                    </Button>
                    <div className="text-center pt-4 border-t">
                      <p className="text-sm text-gray-600">
                        Don&apos;t have an account?{' '}
                        <button
                          type="button"
                          onClick={() => setIsLogin(false)}
                          className="text-teal-600 hover:text-sky-700 font-medium"
                        >
                          Register here
                        </button>
                      </p>
                    </div>
                  </form>
                </CardContent>
              </Card>
            </div>
          ) : (
            // Enhanced Registration Form
            <EnhancedRegistration
              onRegistrationSuccess={(user) => {
                // After successful registration, log the user in
                const token = localStorage.getItem('token');
                onLogin(token, user);
              }}
              onSwitchToLogin={() => setIsLogin(true)}
            />
          )}
        </div>
      </div>
    </div>
  );
}